import { useTranslation } from 'react-i18next';
import BottomNav from "../components/BottomNav";
import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import toast from "react-hot-toast";
import { matchingApi, objectsApi } from "../services/api";
import { useAuth } from "../context/AuthContext";
import Button from "../components/Button";
import Callout from "../components/Callout";

const T = { night:"#0A1F3D", royal:"#1263B0", cyan:"#1CBCD3", gold:"#C9922A",
  goldL:"#FBF4E8", cyanL:"#E0F7FA", white:"#fff", bg:"#F7F9FC",
  border:"#E2E8F0", sub:"#64748B" };

function formatDate(dateStr, lang) {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleDateString(lang === "en" ? "en-US" : "fr-FR", {
      day: "2-digit", month: "long", year: "numeric",
    });
  } catch { return dateStr; }
}

const CRITERIA = [
  ["criteria_category",    "scoreC1Category"],
  ["criteria_description", "scoreC2Description"],
  ["criteria_color",       "scoreC3Color"],
  ["criteria_date",        "scoreC4Date"],
  ["criteria_location",    "scoreC5Location"],
  ["criteria_photos",      "scoreC6Photos"],
];

export default function MatchingConfirm() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("en") ? "en" : "fr";
  const { matchingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [matching, setMatching] = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    const cached = sessionStorage.getItem("matching_" + matchingId);
    if (cached) {
      try { setMatching(JSON.parse(cached)); } catch { /* ignore */ }
    }
    objectsApi.getMyMatchings()
      .then(r => {
        const found = (r.data?.data || []).find(m => m.id === matchingId);
        if (found) setMatching(found);
        else if (!cached) setNotFound(true);
      })
      .catch(() => { if (!cached) setNotFound(true); })
      .finally(() => setLoading(false));
  }, [matchingId]);

  const isOwner  = !!user?.userId && matching?.lostObject?.owner?.id === user.userId;
  const isFinder = !!user?.userId && matching?.foundObject?.finder?.id === user.userId;
  const ownerConfirmed  = !!matching?.ownerConfirmedAt;
  const finderConfirmed = !!matching?.finderConfirmedAt;
  const mutual = matching?.status === "CONFIRME_MUTUEL" || !!matching?.mutuallyConfirmed;
  const strong = matching?.strongMatch ?? (matching?.totalScore ?? 0) >= 90;

  const confirmOwner = async () => {
    setConfirming(true);
    try {
      const res = await matchingApi.confirmOwner(matchingId);
      setMatching(res.data?.data || matching);
      toast.success(t("matching.confirmed"));
    } catch (err) {
      toast.error(err.response?.data?.message || t("matching.error"));
    } finally { setConfirming(false); }
  };

  const confirmFinder = async () => {
    setConfirming(true);
    try {
      const res = await matchingApi.confirmFinder(matchingId);
      setMatching(res.data?.data || matching);
      toast.success(t("matching.confirmed"));
    } catch (err) {
      toast.error(err.response?.data?.message || t("matching.error"));
    } finally { setConfirming(false); }
  };

  if (loading) return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center",
      justifyContent:"center", background:T.bg }}>
      <div style={{ color:T.sub }}>{t("common.loading")}</div>
    </div>
  );

  if (notFound || !matching) return (
    <div style={{ minHeight:"100vh", background:T.bg, fontFamily:"'DM Sans',sans-serif" }}>
      <div style={{ minHeight:"70vh", display:"flex", flexDirection:"column",
        alignItems:"center", justifyContent:"center", padding:"24px", textAlign:"center" }}>
        <div style={{ fontSize:"48px", marginBottom:"16px" }}>🔍</div>
        <h2 style={{ fontFamily:"'Playfair Display',serif", color:T.night, marginBottom:"8px" }}>
          {t("matching.not_found_title")}
        </h2>
        <p style={{ color:T.sub, fontSize:"14px", marginBottom:"20px" }}>
          {t("matching.not_found_desc")}
        </p>
        <Link to="/matchings" style={{ color:T.royal, fontSize:"14px", fontWeight:700, textDecoration:"none" }}>
          {t("matching.back_list")}
        </Link>
      </div>
      <BottomNav />
    </div>
  );

  const score = matching.totalScore ?? 0;
  const levelColor = strong ? T.gold : T.cyan;
  const levelLabel = strong ? t("matching.level_fort") : t("matching.level_probable");

  return (
    <div style={{ minHeight:"100vh", background:T.bg, fontFamily:"'DM Sans',sans-serif" }}>
      {/* Header */}
      <div style={{ background:`linear-gradient(135deg,${levelColor},${strong?"#A67820":"#0F8A9C"})`,
        padding:"16px 24px 28px" }}>
        <div style={{ maxWidth:"680px", margin:"0 auto" }}>
          <div style={{ display:"flex", alignItems:"center", gap:"12px", marginBottom:"16px" }}>
            <button onClick={() => navigate("/matchings")}
              style={{ background:"rgba(255,255,255,0.2)", border:"none", borderRadius:"10px",
                width:"36px", height:"36px", color:"#fff", cursor:"pointer", fontSize:"16px" }}>←</button>
            <span style={{ color:T.white, fontWeight:700, fontSize:"15px" }}>{t("matching.title")}</span>
          </div>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
            <div>
              <span style={{ background:"rgba(255,255,255,0.2)", color:"#fff", fontSize:"11px",
                fontWeight:700, padding:"4px 10px", borderRadius:"20px",
                textTransform:"uppercase", letterSpacing:"0.5px" }}>
                {levelLabel}
              </span>
              <div style={{ fontFamily:"'Playfair Display',serif", fontSize:"26px",
                color:"#fff", fontWeight:700, marginTop:"10px" }}>
                {t("matching.score")} {score} / 100
              </div>
            </div>
            <div style={{ width:"64px", height:"64px", background:"rgba(255,255,255,0.2)",
              borderRadius:"16px", display:"flex", alignItems:"center",
              justifyContent:"center", fontSize:"32px" }}>🎯</div>
          </div>

          {/* Score par critere */}
          <div style={{ display:"flex", gap:"6px", marginTop:"16px", flexWrap:"wrap" }}>
            {CRITERIA.map(([labelKey, field]) => (
              <div key={field} style={{ flex:"1 1 60px", background:"rgba(255,255,255,0.2)",
                borderRadius:"8px", padding:"6px 4px", textAlign:"center" }}>
                <div style={{ fontSize:"13px", fontWeight:700, color:"#fff" }}>
                  {matching[field] ?? 0}
                </div>
                <div style={{ fontSize:"9px", color:"rgba(255,255,255,0.7)", marginTop:"2px" }}>
                  {t(`matching.${labelKey}`)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ maxWidth:"680px", margin:"0 auto", padding:"24px 24px 80px" }}>

        <ObjectDetailCard
          label={t("matching.lost_object")}
          accent={T.royal} accentBg="#EFF6FF"
          obj={matching.lostObject}
          location={matching.lostObject?.lostLocation}
          date={formatDate(matching.lostObject?.lostDate, lang)}
          reporter={matching.lostObject?.owner}
          t={t}
        />
        <ObjectDetailCard
          label={t("matching.found_object")}
          accent={T.gold} accentBg={T.goldL}
          obj={matching.foundObject}
          location={matching.foundObject?.foundLocation}
          date={formatDate(matching.foundObject?.foundDate, lang)}
          reporter={matching.foundObject?.finder}
          t={t}
        />

        {mutual ? (
          <div style={{ background:T.goldL, border:"2px solid rgba(201,146,42,0.3)",
            borderRadius:"16px", padding:"24px", textAlign:"center" }}>
            <div style={{ fontSize:"48px", marginBottom:"12px" }}>✅</div>
            <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:"20px",
              fontWeight:700, color:T.gold, marginBottom:"8px" }}>
              {t("matching.mutual_title")}
            </h3>
            <p style={{ fontSize:"13px", color:T.sub, lineHeight:"1.6", marginBottom:"20px" }}>
              {t("matching.mutual_desc")}
            </p>
            <Link to={`/preuves/${matching.id}`} style={{ textDecoration:"none" }}>
              <Button variant="gold">{t("matching.submit_proof")}</Button>
            </Link>
          </div>
        ) : isOwner ? (
          ownerConfirmed ? (
            <ConfirmedBox waitingText={t("matching.waiting_finder")} />
          ) : (
            <>
              <Callout variant="gold" icon="⚠️">
                <strong>{t("matching.irreversible_title")}</strong>{" "}
                {t("matching.owner_confirm_warning")}
              </Callout>
              <div style={{ display:"flex", gap:"12px" }}>
                <Button variant="danger" onClick={() => navigate("/matchings")}>
                  ✕ {t("matching.reject_btn")}
                </Button>
                <Button variant="gold" full loading={confirming} onClick={confirmOwner}>
                  ✓ {t("matching.confirm_btn")}
                </Button>
              </div>
            </>
          )
        ) : isFinder ? (
          finderConfirmed ? (
            <ConfirmedBox waitingText={t("matching.waiting_owner")} />
          ) : (
            <>
              <Callout variant="cyan" icon="ℹ️">
                {t("matching.finder_confirm_info")}
              </Callout>
              <div style={{ display:"flex", gap:"12px" }}>
                <Button variant="danger" onClick={() => navigate("/matchings")}>
                  ✕ {t("matching.reject_btn")}
                </Button>
                <Button variant="primary" full loading={confirming} onClick={confirmFinder}>
                  ✓ {t("matching.confirm_btn")}
                </Button>
              </div>
            </>
          )
        ) : null}
      </div>
      <BottomNav />
    </div>
  );
}

function ConfirmedBox({ waitingText }) {
  return (
    <div style={{ background:T.cyanL, border:"2px solid rgba(28,188,211,0.3)",
      borderRadius:"16px", padding:"24px", textAlign:"center" }}>
      <div style={{ fontSize:"40px", marginBottom:"10px" }}>⏳</div>
      <p style={{ fontSize:"13px", color:T.night, fontWeight:600 }}>{waitingText}</p>
    </div>
  );
}

function ObjectDetailCard({ label, accent, accentBg, obj, location, date, reporter, t }) {
  if (!obj) return null;
  return (
    <div style={{ background:T.white, borderRadius:"16px",
      border:"1.5px solid #E2E8F0", borderLeft:`4px solid ${accent}`,
      padding:"20px", marginBottom:"16px" }}>
      <span style={{ fontSize:"11px", fontWeight:700, color:accent,
        background:accentBg, padding:"3px 10px", borderRadius:"20px",
        textTransform:"uppercase", letterSpacing:"1px" }}>
        {label}
      </span>
      <div style={{ display:"flex", gap:"16px", marginTop:"14px" }}>
        <div style={{ width:"64px", height:"64px", borderRadius:"14px",
          background:"#F8FAFC", border:"1.5px solid #E2E8F0",
          display:"flex", alignItems:"center", justifyContent:"center",
          fontSize:"28px", flexShrink:0 }}>📦</div>
        <div style={{ flex:1 }}>
          <div style={{ fontWeight:700, fontSize:"16px", color:"#1E293B", marginBottom:"6px" }}>
            {obj.name}
          </div>
          <div style={{ fontSize:"12px", color:T.sub, lineHeight:"1.8" }}>
            {t(`categories.${obj.category}`, { defaultValue: obj.category })}
            {obj.mainColor && <> · {t(`colors.${(obj.mainColor||"").toLowerCase()}`, { defaultValue: obj.mainColor })}</>}
            {obj.brandModel && <> · {obj.brandModel}</>}
            <br/>
            📍 {location || "—"}<br/>
            📅 {date}<br/>
            🔖 {obj.reference}
            {reporter && <><br/>👤 {reporter.firstName} {reporter.lastName?.charAt(0)}.</>}
          </div>
          {obj.description && (
            <div style={{ marginTop:"10px", fontSize:"12px", color:"#1E293B",
              background:"#F8FAFC", borderRadius:"8px", padding:"10px 12px", lineHeight:"1.6" }}>
              {obj.description}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
